import { Switch, Route, useLocation } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { AppLayout } from "@/components/app-layout";
import NotFound from "@/pages/not-found";
import { AuthProvider } from "@/contexts/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { useAdminAccess } from "@/hooks/use-admin-access";
import Login from "@/pages/login";

// CRM Module Pages
import Dashboard from "@/pages/dashboard";
// Sprint 6A - Business Analytics Dashboards
import ExecutiveDashboard from "@/pages/dashboard/executive";
import AccountAnalysisDashboard from "@/pages/dashboard/account-analysis";
import ContactList from "@/pages/contacts/contact-list";
import ContactDetail from "@/pages/contacts/contact-detail";
import AccountList from "@/pages/accounts/account-list";
import AccountDetail from "@/pages/accounts/account-detail";
import ClientGroupList from "@/pages/client-groups/client-group-list";
import ClientGroupDetail from "@/pages/client-groups/client-group-detail";
import ClientGroupWorkbenchV2 from "@/pages/client-groups/client-group-workbench-v2";
import { crmxV2KitEnabled, crmxSettingsEnabled } from "@/lib/feature-flags";
import SettingsShell from "@/pages/settings/settings-shell";
import DealList from "@/pages/deals/deal-list";
import DealDetail from "@/pages/deals/deal-detail";
// Sprint 18 - F008: Pipeline Board View
import PipelineBoardPage from "@/pages/deals/pipeline-board";
import FacilityList from "@/pages/facilities/facility-list";
import FacilityDetail from "@/pages/facilities/facility-detail";
import PartnerList from "@/pages/partners/partner-list";
import PartnerDetail from "@/pages/partners/partner-detail";
// Sprint 4 pages
import LenderList from "@/pages/lenders/lender-list";
import LenderDetail from "@/pages/lenders/lender-detail";
// Sprint 5 pages - Securities standalone restored per ISSUE-160 (admin-only global view)
// Primary access via nested tabs in Facility Detail (ISSUE-086), but standalone routes needed for comprehensive CRUD testing
import SecurityList from "@/pages/securities/securities-list";
import SecurityDetail from "@/pages/securities/security-detail";
// Sprint 7.5 - Deal Tasks Dashboard
import DealTasksDashboard from "@/pages/crm/deal-tasks-dashboard";

// Issues Manager Module Pages
import IssueList from "@/pages/issues/issue-list";
import IssueDetail from "@/pages/issues/issue-detail";
import IssueMetrics from "@/pages/issues/issue-metrics";

// Testing Module Pages
import ChecklistList from "@/pages/testing/checklist-list";
import ChecklistRunner from "@/pages/testing/checklist-runner";

// Test Harness Pages (Sprint 10)
import { FinancialPlPanelTestPage } from "@/pages/test/financial-pl-panel";

// Sprint 20 - Commission Manager Module Pages
import {
  CommissionDashboardPage,
  CommissionTransactionsWrapper,
  CommissionImportPage,
  CommissionMatchingPage,
  CommissionRctiPage,
} from "@/pages/commission/index";

// Sprint 21 - Compliance Manager Module Pages
import ComplaintsPage from "@/pages/compliance/complaints";
import ComplaintDetailPage from "@/pages/compliance/complaint-detail";
import ComplianceCalendarPage from "@/pages/compliance/calendar";
import ComplianceDashboardPage from "@/pages/compliance/dashboard";
import AccPage from "@/pages/compliance/acc";

// Sprint 22 - F006: Credit Representatives
import CreditRepsPage from "@/pages/compliance/credit-reps";
import CreditRepDetailPage from "@/pages/compliance/credit-rep-detail";
// Sprint 22 - F007: Insurance Policies
import InsurancePage from "@/pages/compliance/insurance";
import InsuranceDetailPage from "@/pages/compliance/insurance-detail";
// Sprint 22 - F008: Document Library
import DocumentLibraryPage from "@/pages/compliance/documents";
// Sprint 22 - F009: Memberships & Certificates
import MembershipsPage from "@/pages/compliance/memberships";
// Sprint 22 - F010: Security Audits
import SecurityAuditsPage from "@/pages/compliance/security-audits";
import SecurityAuditDetailPage from "@/pages/compliance/security-audit-detail";

// Sprint 23 - F006: Extraction Preview Page
import ExtractionPreviewPage from "@/pages/financial/extraction-preview-page";

// Sprint 24 - F007: Xero Mapping Editor Page
import XeroMappingPage from "@/pages/financial/xero-mapping-page";

// Sprint Management Pages
import SprintList from "@/pages/sprints/sprint-list";
import SprintDetail from "@/pages/sprints/sprint-detail";

// Project Manager Module Pages
import ProjectManagerDashboard from "@/pages/project-manager/dashboard";
import ProjectManagerTasks from "@/pages/project-manager/tasks";
import ProjectManagerProjects from "@/pages/project-manager/projects";
import ProjectDetail from "@/pages/project-manager/project-detail";
import ProjectManagerPerformance from "@/pages/project-manager/performance";
import Settings from "@/pages/project-manager/settings";
import EmailTriageInboxPage from "@/pages/email/email-triage-inbox";

function AppRouter() {
  const isAdmin = useAdminAccess();
  // Issue #511: Mark every in-app navigation so Back buttons can use history.back()
  const [location] = useLocation();
  useEffect(() => {
    try { sessionStorage.setItem('cameFromApp', 'true'); } catch { /* privacy mode */ }
  }, [location]);

  return (
    <Switch>
      {/* CRM Module Routes */}
      <Route path="/" component={Dashboard} />
      <Route path="/dashboard" component={Dashboard} />
      {/* Sprint 6A - Business Analytics Dashboard Routes */}
      <Route path="/dashboard/executive" component={ExecutiveDashboard} />
      <Route path="/dashboard/account/:accountId" component={AccountAnalysisDashboard} />
      <Route path="/contacts" component={ContactList} />
      <Route path="/contacts/:id" component={ContactDetail} />
      <Route path="/accounts" component={AccountList} />
      <Route path="/accounts/:id" component={AccountDetail} />
      <Route path="/accounts/:id/financials" component={AccountDetail} />
      <Route path="/client-groups" component={ClientGroupList} />
      <Route path="/client-groups/:id" component={crmxV2KitEnabled ? ClientGroupWorkbenchV2 : ClientGroupDetail} />
      {/* Email Intel P1 (EDG-8483) — bulk triage inbox */}
      <Route path="/email/triage" component={EmailTriageInboxPage} />
      <Route path="/deals" component={DealList} />
      {/* Sprint 18 - F008: Pipeline Board (must be before :id) */}
      <Route path="/deals/pipeline" component={PipelineBoardPage} />
      <Route path="/deals/:id" component={DealDetail} />
      <Route path="/facilities" component={FacilityList} />
      <Route path="/facilities/:id" component={FacilityDetail} />
      {/* /scenarios route removed - scenarios are now deals with transaction_stage='scenario' (Q9 Design) */}
      <Route path="/partners" component={PartnerList} />
      <Route path="/partners/:id" component={PartnerDetail} />
      {/* Sprint 4 routes */}
      <Route path="/lenders" component={LenderList} />
      <Route path="/lenders/:id" component={LenderDetail} />
      {/* Sprint 5 routes - Securities standalone routes (admin-only per ISSUE-160) */}
      {/* Primary access via Facility Detail nested tabs (RULE-TAB-012) */}
      <Route path="/securities" component={SecurityList} />
      <Route path="/securities/:id" component={SecurityDetail} />
      {/* Sprint 7.5 - Deal Tasks Dashboard */}
      <Route path="/crm/deal-tasks" component={DealTasksDashboard} />

      {/* Sprint 20 - Commission Manager Routes */}
      <Route path="/commission/dashboard" component={CommissionDashboardPage} />
      <Route path="/commission/transactions" component={CommissionTransactionsWrapper} />
      <Route path="/commission/import" component={CommissionImportPage} />
      <Route path="/commission/matching" component={CommissionMatchingPage} />
      <Route path="/commission/rcti" component={CommissionRctiPage} />
      {/* /commission redirects to transactions (primary view) */}
      <Route path="/commission" component={CommissionTransactionsWrapper} />

      {/* Sprint 21 - Compliance Manager Routes */}
      {/* Specific routes before :id — complaints list before detail */}
      <Route path="/compliance/complaints" component={ComplaintsPage} />
      <Route path="/compliance/complaints/:id" component={ComplaintDetailPage} />
      <Route path="/compliance/calendar" component={ComplianceCalendarPage} />
      <Route path="/compliance/dashboard" component={ComplianceDashboardPage} />
      {/* F010: ACC Checklist */}
      <Route path="/compliance/acc" component={AccPage} />

      {/* Sprint 22 - F006: Credit Representatives */}
      <Route path="/compliance/credit-reps" component={CreditRepsPage} />
      <Route path="/compliance/credit-reps/:id" component={CreditRepDetailPage} />
      {/* Sprint 22 - F007: Insurance Policies */}
      <Route path="/compliance/insurance" component={InsurancePage} />
      <Route path="/compliance/insurance/:id" component={InsuranceDetailPage} />
      {/* Sprint 22 - F008: Document Library */}
      <Route path="/compliance/documents" component={DocumentLibraryPage} />
      {/* Sprint 22 - F009: Memberships & Certificates */}
      <Route path="/compliance/memberships" component={MembershipsPage} />
      {/* Sprint 22 - F010: Security Audits */}
      <Route path="/compliance/security-audits" component={SecurityAuditsPage} />
      <Route path="/compliance/security-audits/:id" component={SecurityAuditDetailPage} />

      {/* Issues Manager Module Routes */}
      <Route path="/issues" component={IssueList} />
      <Route path="/issues/metrics" component={IssueMetrics} />
      <Route path="/issues/:id" component={IssueDetail} />

      {/* Testing Module Routes */}
      <Route path="/project-manager/testing-checklists" component={ChecklistList} />
      <Route path="/project-manager/testing-checklists/:id" component={ChecklistRunner} />

      {/* Test Harness Routes (Sprint 10) */}
      <Route path="/test/financial-pl-panel" component={FinancialPlPanelTestPage} />

      {/* Sprint 23 - F006: Extraction Preview */}
      <Route path="/financial/import/preview/:jobId" component={ExtractionPreviewPage} />

      {/* Sprint 24 - F007: Xero Mapping Editor */}
      <Route path="/financial-periods/:periodId/xero-mapping" component={XeroMappingPage} />

      {/* Sprint Management Routes */}
      <Route path="/project-manager/sprints" component={SprintList} />
      <Route path="/project-manager/sprints/:id" component={SprintDetail} />

      {/* Project Manager Module Routes */}
      <Route path="/project-manager/dashboard" component={ProjectManagerDashboard} />
      <Route path="/project-manager/tasks" component={ProjectManagerTasks} />
      <Route path="/project-manager/projects" component={ProjectManagerProjects} />
      <Route path="/project-manager/projects/:id" component={ProjectDetail} />
      <Route path="/project-manager/performance" component={ProjectManagerPerformance} />
      <Route path="/project-manager/settings" component={isAdmin ? Settings : NotFound} />

      {/* Fallback to 404 */}
      <Route path="/settings" component={crmxV2KitEnabled && crmxSettingsEnabled && isAdmin ? SettingsShell : NotFound} />
      <Route path="/settings/data-hygiene" component={crmxV2KitEnabled && crmxSettingsEnabled && isAdmin ? SettingsShell : NotFound} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  // Custom sidebar width for enterprise application - compact per PRD specs
  const style = {
    "--sidebar-width": "12.5rem",     // 200px for compact navigation
    "--sidebar-width-icon": "3rem",   // 48px icon-only width
  };

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Switch>
            {/* Login route - accessible without authentication */}
            <Route path="/login" component={Login} />

            {/* All other routes - protected by auth */}
            <Route>
              <ProtectedRoute>
                <SidebarProvider style={style as React.CSSProperties}>
                  <div className="flex h-screen w-full">
                    <AppSidebar />
                    <AppLayout showHeader={false}>
                      <AppRouter />
                    </AppLayout>
                  </div>
                </SidebarProvider>
              </ProtectedRoute>
            </Route>
          </Switch>
          <Toaster />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
